// The one question every exit with unsaved edits asks (a docked canvas, a page
// editor such as Knowledge's, or the shell's own navigation).
//
// Namespace `unsaved-edit`; owner: ui/workspace/canvas-leave-dialog.tsx.
// No placeholders. Settings keeps its own wording in the app catalogs.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Exiting will discard your edit',
  description: 'Nothing has been saved yet.',
  keepEditing: 'Keep editing',
  discard: 'Discard edit',
};

export type UnsavedEditCopyKey = keyof typeof en;
type Copy = Record<UnsavedEditCopyKey, string>;

const bg: Copy = {
  title: 'Излизането ще отхвърли редакцията ви',
  description: 'Все още нищо не е запазено.',
  keepEditing: 'Продължете с редактирането',
  discard: 'Отхвърляне на редакцията',
};

const cs: Copy = {
  title: 'Odchodem zahodíte svou úpravu',
  description: 'Zatím nebylo nic uloženo.',
  keepEditing: 'Pokračovat v úpravách',
  discard: 'Zahodit úpravu',
};

const da: Copy = {
  title: 'Hvis du går, kasseres din redigering',
  description: 'Intet er gemt endnu.',
  keepEditing: 'Fortsæt med at redigere',
  discard: 'Kassér redigering',
};

const de: Copy = {
  title: 'Wenn Sie die Seite verlassen, wird Ihre Bearbeitung verworfen',
  description: 'Es wurde noch nichts gespeichert.',
  keepEditing: 'Weiter bearbeiten',
  discard: 'Bearbeitung verwerfen',
};

const el: Copy = {
  title: 'Η έξοδος θα απορρίψει την επεξεργασία σας',
  description: 'Δεν έχει αποθηκευτεί τίποτα ακόμη.',
  keepEditing: 'Συνέχεια επεξεργασίας',
  discard: 'Απόρριψη επεξεργασίας',
};

const es: Copy = {
  title: 'Si sales, se descartará tu edición',
  description: 'Todavía no se ha guardado nada.',
  keepEditing: 'Seguir editando',
  discard: 'Descartar edición',
};

const et: Copy = {
  title: 'Lahkumisel teie muudatus tühistatakse',
  description: 'Midagi pole veel salvestatud.',
  keepEditing: 'Jätka muutmist',
  discard: 'Loobu muudatusest',
};

const fi: Copy = {
  title: 'Poistuminen hylkää muokkauksesi',
  description: 'Mitään ei ole vielä tallennettu.',
  keepEditing: 'Jatka muokkaamista',
  discard: 'Hylkää muokkaus',
};

const fr: Copy = {
  title: 'En quittant, vous perdrez votre modification',
  description: 'Rien n’a encore été enregistré.',
  keepEditing: 'Continuer à modifier',
  discard: 'Abandonner la modification',
};

const ga: Copy = {
  title: 'Má fhágann tú, scriosfar d’eagarthóireacht',
  description: 'Níor sábháladh aon rud fós.',
  keepEditing: 'Lean ort ag eagarthóireacht',
  discard: 'Scrios an eagarthóireacht',
};

const hr: Copy = {
  title: 'Izlaskom ćete odbaciti svoju izmjenu',
  description: 'Još ništa nije spremljeno.',
  keepEditing: 'Nastavi uređivati',
  discard: 'Odbaci izmjenu',
};

const hu: Copy = {
  title: 'Ha kilép, elveti a szerkesztést',
  description: 'Még semmi sincs mentve.',
  keepEditing: 'Szerkesztés folytatása',
  discard: 'Szerkesztés elvetése',
};

const it: Copy = {
  title: 'Uscendo, la modifica andrà persa',
  description: 'Non è stato ancora salvato nulla.',
  keepEditing: 'Continua a modificare',
  discard: 'Scarta la modifica',
};

const lt: Copy = {
  title: 'Išėjus jūsų pakeitimas bus atmestas',
  description: 'Kol kas niekas neišsaugota.',
  keepEditing: 'Tęsti redagavimą',
  discard: 'Atmesti pakeitimą',
};

const lv: Copy = {
  title: 'Izejot jūsu labojums tiks atmests',
  description: 'Nekas vēl nav saglabāts.',
  keepEditing: 'Turpināt rediģēt',
  discard: 'Atmest labojumu',
};

const mt: Copy = {
  title: 'Jekk toħroġ, se tarmi l-bidla tiegħek',
  description: 'Għadu ma ġie salvat xejn.',
  keepEditing: 'Kompli editja',
  discard: 'Armi l-bidla',
};

const nl: Copy = {
  title: 'Als u weggaat, gaat uw bewerking verloren',
  description: 'Er is nog niets opgeslagen.',
  keepEditing: 'Verder bewerken',
  discard: 'Bewerking negeren',
};

const pl: Copy = {
  title: 'Wyjście spowoduje odrzucenie Twojej edycji',
  description: 'Nic nie zostało jeszcze zapisane.',
  keepEditing: 'Kontynuuj edycję',
  discard: 'Odrzuć edycję',
};

const pt: Copy = {
  title: 'Ao sair, a sua edição será descartada',
  description: 'Ainda não foi guardado nada.',
  keepEditing: 'Continuar a editar',
  discard: 'Descartar edição',
};

const ro: Copy = {
  title: 'Dacă ieșiți, modificarea va fi abandonată',
  description: 'Nu s-a salvat încă nimic.',
  keepEditing: 'Continuați editarea',
  discard: 'Renunțați la modificare',
};

const sk: Copy = {
  title: 'Odchodom zahodíte svoju úpravu',
  description: 'Zatiaľ nebolo nič uložené.',
  keepEditing: 'Pokračovať v úpravách',
  discard: 'Zahodiť úpravu',
};

const sl: Copy = {
  title: 'Z izhodom boste zavrgli svoje urejanje',
  description: 'Nič še ni shranjeno.',
  keepEditing: 'Nadaljuj z urejanjem',
  discard: 'Zavrzi urejanje',
};

const sv: Copy = {
  title: 'Om du lämnar kastas din ändring',
  description: 'Inget har sparats ännu.',
  keepEditing: 'Fortsätt redigera',
  discard: 'Kasta ändringen',
};

export const unsavedEditCopy: NamespaceCatalog<UnsavedEditCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default unsavedEditCopy;
